import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';

interface CompositeStatesMapImageProps {
  user: {
    firstName?: string;
    lastName?: string;
    username: string;
    publicUsername?: string;
  };
  visitedStates: string[];
  stateStats: { [state: string]: number };
  width?: number;
  height?: number;
}

const CompositeStatesMapImage: React.FC<CompositeStatesMapImageProps> = ({
  user,
  visitedStates,
  stateStats,
  width = 800,
  height = 500
}) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const generateMapImage = async () => {
      if (!user?.username) {
        setError('User information not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Use the public username if available, otherwise fall back to username
        const identifier = user.publicUsername || user.username;
        console.log('User object:', user);
        console.log('Using identifier:', identifier);
        
        // Add timestamp to prevent caching issues
        const timestamp = Date.now();
        
        // Use API base URL pattern consistent with rest of codebase
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
        const baseUrl = API_BASE_URL.replace('/api', '');
        const mapUrl = `${baseUrl}/api/public/composite-states-map/${identifier}?width=${width}&height=${height}&expanded=true&t=${timestamp}`;
        
        console.log('Loading composite states map from:', mapUrl);
        
        // Test if the endpoint returns a valid image
        const response = await fetch(mapUrl);
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('Error response body:', errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        // Check if response is actually an image
        const contentType = response.headers.get('content-type');
        console.log('Content-Type:', contentType);
        
        if (!contentType?.startsWith('image/')) {
          const errorText = await response.text();
          console.log('Non-image response body:', errorText);
          throw new Error(`Expected image, got: ${contentType}. Response: ${errorText.substring(0, 500)}`);
        }
        
        // Convert response to blob and create object URL to avoid CORS issues
        const imageBlob = await response.blob();
        const blobUrl = URL.createObjectURL(imageBlob);
        console.log('Created blob URL:', blobUrl);
        
        setImageUrl(blobUrl);
        setLoading(false);

      } catch (error) {
        console.error('Error loading composite states map:', error);
        setError(`Failed to load states map: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    generateMapImage();
  }, [user?.username, user?.publicUsername, width, height]);

  // Cleanup blob URL when component unmounts or imageUrl changes
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading states map...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, p: 2 }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  const displayName = user.firstName || user.publicUsername || user.username;
  const stateCount = visitedStates.length;
  const totalMemories = Object.values(stateStats).reduce((sum, count) => sum + count, 0);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Composite States Map Image */}
      <Box
        component="img"
        src={imageUrl}
        alt={`${displayName}'s visited states map`}
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        onError={(e) => {
          console.error('Image failed to load:', imageUrl);
          console.error('Image error event:', e);
          setError('Failed to display map image');
        }}
        onLoad={() => {
          console.log('Image loaded successfully:', imageUrl);
        }}
      />
    </Box>
  );
};

export default CompositeStatesMapImage;